import { useCallback, useEffect, useRef, useState, type DragEvent as ReactDragEvent, type MouseEvent as ReactMouseEvent } from "react";
import { api, type Organization, type OrgSettings } from "../api/client";
import { useVault } from "../vault/store";
import { b64decode, b64encode, newOrgKey, newRecoveryKeypair, wrapOrgKeyTo, acceptSharedItemKey, decryptItem, encryptName, encryptMeta } from "../crypto/vault";
import {
  decryptSummary,
  loadItems,
  createItem,
  normalizeContent,
  patchItem,
  filterMatches,
  filterIsActive,
  allTags,
  EMPTY_FILTER,
  type DecryptedItem,
  type ItemFilter,
} from "../vault/items";
import { loadTemplates, saveTemplate, deleteTemplate, skeletonFromItem, contentFromTemplate, type VaultTemplate } from "../vault/templates";
import { templateFor } from "../vault/catalog";
import { type EditorState } from "./ItemEditor";
import { type BulkEdit } from "./BulkEditDialog";
import { type DeviceMeta } from "../vault/devices";
import { useToast } from "./toast-context";
import { useIsOffline } from "../net/connectivity";
import { useOffline, offlineItemsFor, offlineFoldersFor } from "../vault/offline";
import { getFavorites, getRecent, pushRecent } from "../vault/prefs";
import { buildFolderTree, safeMeta, safeName, type FolderNode } from "./folder-tree";
import { useT } from "../i18n";

/**
 * The vault workspace: every piece of AppShell state/logic that concerns the *data* domain —
 * orgs, items, folder/device tree, selection, search facets, templates, trash, bulk ops, DnD,
 * and the vault-scoped dialog flags. Returns a flat object that AppShell destructures into the
 * exact names its JSX has always used, so the extraction is render-identical.
 *
 * Effect order is load-bearing and mirrors the original component: nowMs tick → offline-seed →
 * initial org/health load → personal-vault auto-select → per-vault collapsed-tree restore.
 * `recentIds` is deliberately a plain render-time read: it refreshes because selectItem also
 * sets selectedId (state → re-render → fresh getRecent()).
 */
export function useVaultWorkspace() {
  const vault = useVault();
  const t = useT();
  const toast = useToast();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [items, setItems] = useState<DecryptedItem[]>([]);
  const [folders, setFolders] = useState<FolderNode[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [facets, setFacets] = useState<ItemFilter>(EMPTY_FILTER);
  const [templates, setTemplates] = useState<VaultTemplate[]>([]);
  const [bulkEditing, setBulkEditing] = useState(false);
  // "Now" for the modified-date filter, captured in state (Date.now() is impure in render) and
  // refreshed while a date window is active so the boundary stays current.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [health, setHealth] = useState("…");
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [recoveryKeyShown, setRecoveryKeyShown] = useState<string | null>(null);
  const [orgSettings, setOrgSettings] = useState<OrgSettings | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [movingIds, setMovingIds] = useState<Set<string> | null>(null);
  const [newNode, setNewNode] = useState<{ kind: "folder" | "device"; parentId: string | null } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editNode, setEditNode] = useState<{ id: string; kind: "folder" | "device"; name: string; category?: string; address?: string } | null>(null);
  const anchorRef = useRef<string | null>(null);
  const dragIdsRef = useRef<string[]>([]);
  const dragKindRef = useRef<"item" | "node" | null>(null);
  const dragNodeRef = useRef<string | null>(null);
  const [orgCaps, setOrgCaps] = useState<Set<string>>(new Set());
  // Offline mode (§15): "offline" = rendering the local read-only snapshot (no server keys);
  // "liveOffline" = an online session whose connectivity just dropped. Both are read-only.
  const liveOffline = useIsOffline();
  const offline = vault.phase === "offline-unlocked";
  const readOnly = offline || liveOffline;
  const offlineOrgs = useOffline((s) => s.orgs);
  const [sharing, setSharing] = useState<string | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [mode, setMode] = useState<"org" | "shared" | "trash">("org");
  const [filter, setFilter] = useState<"all" | "fav" | "recent">("all");
  const [favorites, setFavorites] = useState<Set<string>>(() => getFavorites());

  // Org-key accessor for AppShell's JSX sites (dialogs, attachments, editor, health scan). The
  // moved functions below keep calling vault.orgKeyFor directly (their local `const orgKey = …`
  // would otherwise shadow this binding).
  const orgKeyFor = useCallback((org: Organization) => vault.orgKeyFor(org), [vault]);

  // Keep the modified-date filter boundary fresh while a date window is selected (re-tick each min).
  useEffect(() => {
    if (facets.modifiedWithinDays == null) return;
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [facets.modifiedWithinDays]);

  // Apply a filter change and, when a date window is (re)selected, snap "now" so the boundary is
  // immediately current (the interval above only keeps it fresh thereafter).
  const updateFacets = useCallback((f: ItemFilter) => {
    if (f.modifiedWithinDays != null) setNowMs(Date.now());
    setFacets(f);
  }, []);

  const selected = items.find((i) => i.id === selectedId) ?? null;

  const refreshOrgs = useCallback(async () => {
    const { organizations } = await api.listOrgs();
    setOrgs(organizations);
    return organizations;
  }, []);

  // Offline (read-only snapshot): seed a synthetic vault from the local snapshot and skip all
  // server loads. The items are already decrypted in the offline store.
  // Offline: seed the real vault list from the snapshot; the auto-select effect then opens one and
  // openOrg (offline branch) fills items + folder/device tree — so offline mirrors online.
  useEffect(() => {
    if (!offline) return;
    /* eslint-disable react-hooks/set-state-in-effect -- intentional one-shot seeding of local vault
       state from the already-decrypted offline snapshot when entering offline mode */
    setOrgs(offlineOrgs);
    setMode("org");
    setHealth("offline");
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [offline, offlineOrgs]);

  useEffect(() => {
    if (offline) return; // offline snapshot is seeded above; no server calls
    let alive = true;
    async function load() {
      try {
        let { organizations } = await api.listOrgs();
        // Ensure the user has a personal vault (§ personal vault). The org_key is generated
        // and wrapped to the user's own pubkey here (zero-knowledge); the endpoint is
        // idempotent, so a stale/duplicate call just returns the existing vault.
        const v = useVault.getState();
        if (v.keys && v.me?.public_key && !organizations.some((o) => o.is_personal)) {
          try {
            const orgKey = newOrgKey();
            const wrapped = wrapOrgKeyTo(b64decode(v.me.public_key), orgKey);
            await api.createPersonalOrg(b64encode(wrapped));
            organizations = (await api.listOrgs()).organizations;
          } catch {
            /* non-fatal: user can still use shared orgs */
          }
        }
        if (alive) setOrgs(organizations);
      } catch {
        /* ignore */
      }
      try {
        const h = await api.health();
        if (alive) setHealth(h.status);
      } catch {
        if (alive) setHealth("offline");
      }
    }
    void load();
    return () => {
      alive = false;
    };
  }, []);

  const openShared = useCallback(async () => {
    setMode("shared");
    // Leaving the vault would otherwise unmount the item editor (DialogHost needs a selectedOrg)
    // while `editor` stayed set — a zombie that later remounted blank. Closing it here parks any
    // unsaved content in vault/draft.ts, so the restore banner can offer it back.
    setEditor(null);
    setSelectedOrg(null);
    setSelectedId(null);
    const keys = vault.keys;
    if (!keys) {
      setItems([]);
      return;
    }
    try {
      const { shares } = await api.listShares();
      setItems(
        shares.map((sh) => {
          try {
            const itemKey = acceptSharedItemKey(keys.privateKey, b64decode(sh.wrapped_item_key));
            return { id: sh.item_id, type: sh.type, revision: 0, folderId: null, content: normalizeContent(decryptItem(itemKey, b64decode(sh.enc_content))), offlineAllowed: false };
          } catch {
            return { id: sh.item_id, type: sh.type, revision: 0, folderId: null, content: { name: "⚠ cannot decrypt", fields: [] }, offlineAllowed: false };
          }
        }),
      );
    } catch {
      setItems([]);
    }
  }, [vault.keys]);

  const openOrg = useCallback(
    async (org: Organization, keepFolder = false) => {
      setMode("org");
      // Switching vaults retargets the editor's destination under the user — close it (any unsaved
      // content parks in vault/draft.ts and is offered back by the restore banner).
      setEditor(null);
      setSelectedOrg(org);
      setSelectedId(null);
      if (!keepFolder) setSelectedFolderId(null);
      // Offline: fill items + folder/device tree from the local snapshot (no server, no orgKeyFor).
      if (vault.phase === "offline-unlocked") {
        setItems(offlineItemsFor(org.id));
        setFolders(buildFolderTree(offlineFoldersFor(org.id)));
        setTemplates([]);
        setOrgSettings(null);
        setOrgCaps(new Set());
        return;
      }
      try {
        const orgKey = vault.orgKeyFor(org);
        const [its, fres] = await Promise.all([loadItems(org.id, orgKey), api.listFolders(org.id)]);
        setItems(its);
        api.getOrgSettings(org.id).then(setOrgSettings).catch(() => setOrgSettings(null));
        const decoded = fres.folders.map((f) => ({
          id: f.id,
          parentId: f.parent_folder_id ?? null,
          name: safeName(orgKey, f.enc_name),
          kind: f.node_kind ?? "folder",
          meta: f.node_kind === "device" && f.enc_meta ? safeMeta(orgKey, f.enc_meta) : null,
        }));
        setFolders(buildFolderTree(decoded));
        loadTemplates(org.id, orgKey).then(setTemplates).catch(() => setTemplates([]));
      } catch {
        setItems([]);
        setFolders([]);
        setTemplates([]);
      }
      try {
        const { capabilities } = await api.orgCapabilities(org.id);
        setOrgCaps(new Set(capabilities));
      } catch {
        setOrgCaps(new Set());
      }
    },
    [vault],
  );

  // Auto-select the personal vault as the default landing (first load only).
  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (didAutoSelect.current || selectedOrg || mode !== "org") return;
    const personal = orgs.find((o) => o.is_personal);
    if (personal) {
      didAutoSelect.current = true;
      queueMicrotask(() => void openOrg(personal));
    }
  }, [orgs, selectedOrg, mode, openOrg]);

  async function createOrg() {
    const name = window.prompt("New vault (organization) name:");
    if (!name || !vault.me?.public_key) return;
    const orgKey = newOrgKey();
    const wrapped = wrapOrgKeyTo(b64decode(vault.me.public_key), orgKey);
    const org = await api.createOrg(name, b64encode(wrapped));
    // Admin recovery is ON by default (§17.1): wrap org_key to a fresh recovery keypair; the
    // private recovery key is shown to the owner once and must be stored offline.
    try {
      const rk = newRecoveryKeypair();
      const recWrap = wrapOrgKeyTo(rk.publicKey, orgKey);
      await api.setRecovery(org.id, {
        opt_out: false,
        recovery_public_key: b64encode(rk.publicKey),
        wrapped_org_key: b64encode(recWrap),
      });
      setRecoveryKeyShown(b64encode(rk.privateKey));
    } catch {
      /* org still created; recovery can be set later from Admin */
    }
    const list = await refreshOrgs();
    const fresh = list.find((o) => o.id === org.id);
    if (fresh) await openOrg(fresh);
  }

  async function openTrash(org: Organization) {
    setMode("trash");
    setSelectedOrg(org);
    setSelectedId(null);
    try {
      const orgKey = vault.orgKeyFor(org);
      const { items: trashed } = await api.listTrash(org.id);
      setItems(trashed.map((s) => decryptSummary(orgKey, s)));
    } catch {
      setItems([]);
    }
  }

  async function restoreFromTrash() {
    if (!selectedId) return;
    await api.restoreItem(selectedId);
    if (selectedOrg) await openTrash(selectedOrg);
  }

  async function reloadItems() {
    if (offline || !selectedOrg) return; // offline snapshot is read-only; nothing to reload
    if (mode === "trash") await openTrash(selectedOrg);
    else await openOrg(selectedOrg, true);
  }

  // Create a folder or device node under `parentId` (a tree "+", inline dialog — no prompts).
  async function submitNewNode(name: string, category?: string, address?: string) {
    if (!selectedOrg || !newNode) return;
    const orgKey = vault.orgKeyFor(selectedOrg);
    const encMeta =
      newNode.kind === "device"
        ? b64encode(encryptMeta(orgKey, { category: category || "Other", ...(address ? { address } : {}) } as DeviceMeta))
        : undefined;
    await api.createFolder(selectedOrg.id, b64encode(encryptName(orgKey, name)), newNode.parentId ?? undefined, newNode.kind, encMeta);
    setNewNode(null);
    await openOrg(selectedOrg, true);
  }

  // --- Per-item mutations (used by row hover, right-click, and the details pane) ---

  async function duplicateItem(it: DecryptedItem) {
    if (!selectedOrg) return;
    const orgKey = vault.orgKeyFor(selectedOrg);
    await createItem(selectedOrg.id, orgKey, it.type, { ...it.content, name: `${it.content.name} (copy)` }, it.folderId, it.offlineAllowed);
    await reloadItems();
    toast.success(t("Item duplicated"));
  }

  async function trashItem(it: DecryptedItem) {
    if (!window.confirm(`${t("Move this item to trash?")}\n\n${it.content.name}`)) return;
    await api.deleteItem(it.id);
    await reloadItems();
    toast.success(t("Moved to trash"));
  }

  async function restoreItem(it: DecryptedItem) {
    await api.restoreItem(it.id);
    await reloadItems();
    toast.success(t("Item restored"));
  }

  function selectItem(id: string) {
    setSelectedId(id);
    setSelectedIds(new Set([id]));
    anchorRef.current = id;
    pushRecent(id);
  }

  // Click with modifiers: Ctrl/Cmd toggles, Shift selects a range over the visible list.
  function clickItem(it: DecryptedItem, e: ReactMouseEvent) {
    if (e.metaKey || e.ctrlKey) {
      const next = new Set(selectedIds);
      if (next.has(it.id)) next.delete(it.id);
      else next.add(it.id);
      setSelectedIds(next);
      setSelectedId(it.id);
      anchorRef.current = it.id;
    } else if (e.shiftKey && anchorRef.current) {
      const ids = visible.map((v) => v.id);
      const a = ids.indexOf(anchorRef.current);
      const b = ids.indexOf(it.id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        setSelectedIds(new Set(ids.slice(lo, hi + 1)));
      }
      setSelectedId(it.id);
    } else {
      selectItem(it.id);
    }
  }

  function toggleOne(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
    setSelectedId(id);
    anchorRef.current = id;
  }

  // --- Drag-and-drop: items (bulk move) and folder/device nodes (reparent), same vault ---
  function onItemDragStart(it: DecryptedItem, e: ReactDragEvent) {
    const ids = selectedIds.has(it.id) && selectedIds.size > 1 ? [...selectedIds] : [it.id];
    if (!selectedIds.has(it.id)) selectItem(it.id);
    dragKindRef.current = "item";
    dragIdsRef.current = ids;
    e.dataTransfer.setData("text/plain", ids.join(","));
    e.dataTransfer.effectAllowed = "move";
  }
  function onNodeDragStartTree(nodeId: string, e: ReactDragEvent) {
    dragKindRef.current = "node";
    dragNodeRef.current = nodeId;
    e.dataTransfer.setData("text/plain", nodeId);
    e.dataTransfer.effectAllowed = "move";
    e.stopPropagation();
  }
  // True if `target` is the dragged node itself or one of its descendants (invalid reparent).
  function isSelfOrDescendant(nodeId: string, target: string): boolean {
    if (target === nodeId) return true;
    let cur: string | null = target;
    const parentOf = new Map(folders.map((f) => [f.id, f.parentId]));
    while (cur) {
      if (cur === nodeId) return true;
      cur = parentOf.get(cur) ?? null;
    }
    return false;
  }
  function onNodeDragOver(e: ReactDragEvent, target: string) {
    if (dragKindRef.current === "node") {
      const node = dragNodeRef.current;
      const tgt = target === "__root__" ? null : target;
      if (!node || (tgt && isSelfOrDescendant(node, tgt))) return; // invalid → no drop
    } else if (dragKindRef.current !== "item") {
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTarget !== target) setDropTarget(target);
  }
  async function onNodeDrop(e: ReactDragEvent, target: string) {
    e.preventDefault();
    setDropTarget(null);
    const folderId = target === "__root__" ? undefined : target;
    if (dragKindRef.current === "node") {
      const node = dragNodeRef.current;
      dragNodeRef.current = null;
      dragKindRef.current = null;
      if (!node || (folderId && isSelfOrDescendant(node, folderId))) return;
      try {
        await api.moveFolder(node, folderId);
        if (selectedOrg) await openOrg(selectedOrg, true);
      } catch (err) {
        window.alert(err instanceof Error ? err.message : String(err));
      }
      return;
    }
    const ids = dragIdsRef.current;
    dragIdsRef.current = [];
    dragKindRef.current = null;
    if (ids.length === 0) return;
    for (const id of ids) {
      try {
        await api.moveItem(id, folderId);
      } catch {
        /* skip */
      }
    }
    await reloadItems();
  }

  function toggleCollapse(id: string) {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCollapsed(next);
    if (selectedOrg) localStorage.setItem(`tree-collapsed:${selectedOrg.id}`, JSON.stringify([...next]));
  }

  async function submitEditNode(name: string, category?: string, address?: string) {
    if (!selectedOrg || !editNode) return;
    const orgKey = vault.orgKeyFor(selectedOrg);
    const encMeta =
      editNode.kind === "device"
        ? b64encode(encryptMeta(orgKey, { category: category || "Other", ...(address ? { address } : {}) } as DeviceMeta))
        : undefined;
    await api.updateFolder(editNode.id, b64encode(encryptName(orgKey, name)), encMeta);
    setEditNode(null);
    await openOrg(selectedOrg, true);
  }
  async function bulkDelete() {
    if (!window.confirm(`${t("Move this item to trash?")} (${selectedIds.size})`)) return;
    for (const id of selectedIds) {
      try {
        await api.deleteItem(id);
      } catch {
        /* skip */
      }
    }
    const n = selectedIds.size;
    setSelectedIds(new Set());
    await reloadItems();
    toast.success(`${t("Moved to trash")} (${n})`);
  }

  async function deleteFolderUI(id: string) {
    if (!selectedOrg) return;
    if (!window.confirm("Delete this folder? Items inside are not deleted.")) return;
    await api.deleteFolder(id);
    if (selectedFolderId === id) setSelectedFolderId(null);
    await openOrg(selectedOrg, true);
  }

  async function moveSelected(folderId: string) {
    if (!selectedId) return;
    await api.moveItem(selectedId, folderId || undefined);
    await reloadItems();
  }

  // Restore the per-vault collapsed-folder state.
  useEffect(() => {
    if (!selectedOrg) return;
    let next = new Set<string>();
    try {
      const raw = localStorage.getItem(`tree-collapsed:${selectedOrg.id}`);
      if (raw) next = new Set<string>(JSON.parse(raw) as string[]);
    } catch {
      /* ignore */
    }
    queueMicrotask(() => setCollapsed(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg?.id]);

  const recentIds = getRecent();
  const searching = filterIsActive(facets);
  const visible = items.filter((i) => {
    if (!filterMatches(i, facets, nowMs)) return false;
    if (filter === "fav") return favorites.has(i.id);
    if (filter === "recent") return recentIds.includes(i.id);
    // A search/filter spans the whole vault; otherwise show only the selected folder/device.
    if (mode === "org" && !searching && (i.folderId ?? null) !== selectedFolderId) return false;
    return true;
  });

  // Filter-bar facets: item types present (with counts) + distinct tags, derived from the loaded set.
  const typeCounts = new Map<string, number>();
  for (const it of items) typeCounts.set(it.type, (typeCounts.get(it.type) ?? 0) + 1);
  const typeFacets = [...typeCounts.entries()]
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => templateFor(a.type).label.localeCompare(templateFor(b.type).label));
  const tagFacets = allTags(items);

  // Create a new item seeded from a per-org template (into the given parent folder).
  function openFromTemplate(tmpl: VaultTemplate, parentId: string | null) {
    setSelectedFolderId(parentId);
    setEditor({ mode: "new", seed: contentFromTemplate(tmpl) });
  }

  // Save an item's structure (type + fields + tags, secrets stripped) as a shared org template.
  async function saveAsTemplate(it: DecryptedItem) {
    if (!selectedOrg) return;
    const { name, meta } = skeletonFromItem(it);
    const tmplName = window.prompt(t("Template name"), name);
    if (!tmplName?.trim()) return;
    const orgKey = vault.orgKeyFor(selectedOrg);
    try {
      await saveTemplate(selectedOrg.id, orgKey, tmplName.trim(), meta);
      setTemplates(await loadTemplates(selectedOrg.id, orgKey));
      toast.success(t("Template saved"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("Save failed"));
    }
  }

  async function removeTemplate(tmpl: VaultTemplate) {
    if (!selectedOrg) return;
    if (!window.confirm(`${t("Delete this template?")}\n\n${tmpl.name}`)) return;
    await deleteTemplate(tmpl.id);
    setTemplates((ts) => ts.filter((x) => x.id !== tmpl.id));
  }

  // Apply a bulk edit to every selected item: tag add/remove + offline flag (one re-encrypt each),
  // then folder move. Each item is independent; a single failure is skipped, not fatal.
  async function applyBulkEdit(edit: BulkEdit) {
    if (!selectedOrg) return;
    const orgKey = vault.orgKeyFor(selectedOrg);
    const ids = [...selectedIds];
    setBulkEditing(false);
    const editsContent = edit.addTags.length > 0 || edit.removeTags.length > 0 || edit.offline !== null;
    for (const id of ids) {
      try {
        if (editsContent) {
          await patchItem(
            orgKey,
            id,
            (c) => {
              const tags = new Set(c.tags ?? []);
              for (const tg of edit.addTags) tags.add(tg);
              for (const tg of edit.removeTags) tags.delete(tg);
              return { ...c, tags: [...tags] };
            },
            edit.offline ?? undefined,
          );
        }
        if (edit.moveFolderId !== undefined) await api.moveItem(id, edit.moveFolderId ?? undefined);
      } catch {
        /* skip this item */
      }
    }
    setSelectedIds(new Set());
    await reloadItems();
    toast.success(`${t("Bulk edit applied")} (${ids.length})`);
  }

  // Tree helpers: recursive item counts, child lookup, collapse visibility, and folder paths.
  const childrenOf = new Map<string | null, FolderNode[]>();
  for (const f of folders) {
    const arr = childrenOf.get(f.parentId) ?? [];
    arr.push(f);
    childrenOf.set(f.parentId, arr);
  }
  const directCount = new Map<string, number>();
  for (const it of items) if (it.folderId) directCount.set(it.folderId, (directCount.get(it.folderId) ?? 0) + 1);
  const countMemo = new Map<string, number>();
  const nodeCount = (id: string): number => {
    if (countMemo.has(id)) return countMemo.get(id)!;
    let n = directCount.get(id) ?? 0;
    for (const c of childrenOf.get(id) ?? []) n += nodeCount(c.id);
    countMemo.set(id, n);
    return n;
  };
  const folderById = new Map(folders.map((f) => [f.id, f]));
  const nodeHidden = (f: FolderNode): boolean => {
    let cur = f.parentId;
    while (cur) {
      if (collapsed.has(cur)) return true;
      cur = folderById.get(cur)?.parentId ?? null;
    }
    return false;
  };
  const folderPath = (id: string | null): string => {
    const parts: string[] = [];
    let cur = id;
    while (cur) {
      const f = folderById.get(cur);
      if (!f) break;
      parts.unshift(f.name);
      cur = f.parentId;
    }
    return parts.join(" › ");
  };
  const breadcrumb = (id: string | null): { id: string; name: string }[] => {
    const out: { id: string; name: string }[] = [];
    let cur = id;
    while (cur) {
      const f = folderById.get(cur);
      if (!f) break;
      out.unshift({ id: f.id, name: f.name });
      cur = f.parentId;
    }
    return out;
  };
  const selectedNode = selectedFolderId ? folderById.get(selectedFolderId) ?? null : null;

  // Dashboard-derived lists (current vault): recent (resolved + ordered), favorites, node counts.
  const recentItems = recentIds.map((id) => items.find((it) => it.id === id)).filter((x): x is DecryptedItem => !!x);
  const favoriteItems = items.filter((it) => favorites.has(it.id));
  const deviceCount = folders.filter((f) => f.kind === "device").length;
  const folderCount = folders.filter((f) => f.kind === "folder").length;
  // The vault root shows the dashboard band (above the root list) when nothing narrows the view.
  const showDashboard = !readOnly && mode === "org" && !!selectedOrg && !selectedFolderId && !searching && filter === "all";

  const personalOrg = orgs.find((o) => o.is_personal) ?? null;
  const clientOrgs = orgs.filter((o) => !o.is_personal);

  return {
    // status
    offline,
    liveOffline,
    readOnly,
    health,
    // orgs & modes
    orgs,
    personalOrg,
    clientOrgs,
    selectedOrg,
    mode,
    filter,
    setFilter,
    orgCaps,
    orgSettings,
    setOrgSettings,
    orgKeyFor,
    // items & selection
    items,
    visible,
    selected,
    selectedId,
    setSelectedId,
    selectedIds,
    setSelectedIds,
    selectItem,
    clickItem,
    toggleOne,
    // folder/device tree
    folders,
    selectedFolderId,
    setSelectedFolderId,
    collapsed,
    toggleCollapse,
    childrenOf,
    nodeCount,
    nodeHidden,
    folderPath,
    breadcrumb,
    selectedNode,
    deviceCount,
    folderCount,
    // search & facets
    facets,
    setFacets,
    updateFacets,
    searching,
    typeFacets,
    tagFacets,
    // favorites / recent / dashboard
    favorites,
    setFavorites,
    recentItems,
    favoriteItems,
    showDashboard,
    // loaders & mutations
    openOrg,
    openShared,
    openTrash,
    createOrg,
    restoreFromTrash,
    reloadItems,
    moveSelected,
    deleteFolderUI,
    bulkDelete,
    applyBulkEdit,
    duplicateItem,
    trashItem,
    restoreItem,
    // templates
    templates,
    openFromTemplate,
    saveAsTemplate,
    removeTemplate,
    // drag & drop
    dropTarget,
    onItemDragStart,
    onNodeDragStartTree,
    onNodeDragOver,
    onNodeDrop,
    // vault-scoped dialogs
    editor,
    setEditor,
    importing,
    setImporting,
    exporting,
    setExporting,
    healthOpen,
    setHealthOpen,
    settingsOpen,
    setSettingsOpen,
    bulkEditing,
    setBulkEditing,
    sharing,
    setSharing,
    historyId,
    setHistoryId,
    recoveryKeyShown,
    setRecoveryKeyShown,
    movingIds,
    setMovingIds,
    newNode,
    setNewNode,
    editNode,
    setEditNode,
    submitNewNode,
    submitEditNode,
  };
}
